import { useState } from "react";
import { Modal } from "./TMPanels";
import AddressAutocomplete from "./AddressAutocomplete";
import { endpoints } from "../lib/api";
import { put, queueSync, STORES } from "../lib/db";

/* Inline "+ Create client" modal used inside the Job Details step. */
export default function ClientInlineModal({ open, onClose, type = "customer", onCreated }) {
    const [form, setForm] = useState({
        name: "", contact_name: "", email: "", phone: "", address: "",
        policy_number: "", claim_number: "", notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit() {
        if (!form.name.trim()) {
            setErr("Name is required.");
            return;
        }
        setBusy(true);
        setErr("");
        const payload = {
            id: crypto.randomUUID(),
            type,
            ...form,
            name: form.name.trim(),
        };
        try {
            const { data } = await endpoints.saveClient(payload);
            await put(STORES.clients, data);
            onCreated(data);
            reset();
            onClose();
        } catch (e) {
            // offline → queue + cache locally
            try {
                await put(STORES.clients, payload);
                await queueSync("clients", payload);
                onCreated(payload);
                reset();
                onClose();
            } catch (e2) {
                setErr(e2?.message || "Could not save client.");
            }
        } finally {
            setBusy(false);
        }
    }

    function reset() {
        setForm({
            name: "", contact_name: "", email: "", phone: "", address: "",
            policy_number: "", claim_number: "", notes: "",
        });
        setErr("");
    }

    const title = type === "insurer" ? "Add insurer" : "Add client";

    return (
        <Modal
            open={open}
            onClose={() => { reset(); onClose(); }}
            title={title}
            subtitle={`Quick-create a new ${type} without leaving the estimate.`}
            footer={
                <>
                    <div className="text-xs text-[var(--rom-danger)]" data-testid="client-inline-error">{err}</div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => { reset(); onClose(); }} className="btn btn-ghost" disabled={busy}>Cancel</button>
                        <button onClick={submit} className="btn btn-primary" disabled={busy} data-testid="client-inline-save">
                            {busy ? "Saving…" : `Save ${type}`}
                        </button>
                    </div>
                </>
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Name" required>
                    <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus data-testid="client-inline-name" />
                </Field>
                <Field label="Contact person">
                    <input className="input" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="client-inline-contact" />
                </Field>
                <Field label="Email">
                    <input type="email" className="input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="client-inline-email" />
                </Field>
                <Field label="Phone">
                    <input className="input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="client-inline-phone" />
                </Field>
                <Field label="Address" full>
                    <AddressAutocomplete
                        value={form.address}
                        onChange={(v) => set("address", v)}
                        onPick={(parts) => set("address", parts.full || parts.address)}
                        placeholder="Start typing an address…"
                        testId="client-inline-address"
                    />
                </Field>
                {type === "insurer" ? (
                    <Field label="Policy #">
                        <input className="input" value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} data-testid="client-inline-policy" />
                    </Field>
                ) : (
                    <Field label="Claim #">
                        <input className="input" value={form.claim_number} onChange={(e) => set("claim_number", e.target.value)} data-testid="client-inline-claim" />
                    </Field>
                )}
                <Field label="Notes" full>
                    <textarea className="input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="client-inline-notes" />
                </Field>
            </div>
        </Modal>
    );
}

function Field({ label, required, full, children }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="label flex items-center gap-1">
                {label}
                {required && <span className="text-[var(--rom-danger)] font-bold">*</span>}
            </label>
            {children}
        </div>
    );
}
